import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export function SearchOverlay({ open, onClose }) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      const tm = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(tm);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !q.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    const tm = setTimeout(() => {
      api
        .get("/products", { params: { q: q.trim() } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(tm);
  }, [q, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div
        className="absolute inset-0 bg-[#09090B]/60"
        onClick={onClose}
        data-testid="search-overlay-backdrop"
      />
      <div
        data-testid="search-overlay"
        className="absolute top-0 inset-x-0 bg-white border-b-2 border-[#09090B] max-h-[85vh] overflow-y-auto"
      >
        <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <p className="font-badge text-xs text-[var(--zjz-accent)]">{t("searchTitle")}</p>
            <button
              onClick={onClose}
              data-testid="search-close-button"
              aria-label="Close"
              className="hover:text-[var(--zjz-accent)] transition-colors duration-150"
            >
              <X size={22} strokeWidth={1.5} />
            </button>
          </div>
          <div className="relative">
            <Search size={18} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input
              ref={inputRef}
              data-testid="search-overlay-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("searchPh")}
              className="w-full bg-[#F4F3EE] border border-[#09090B] rounded-xl pl-11 pr-4 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-[#09090B] placeholder:text-[#52525B]"
            />
          </div>
          {loading && (
            <p className="font-badge text-xs text-[#52525B] mt-4" data-testid="search-loading">
              {t("loading")}
            </p>
          )}
          {results && !loading && results.length === 0 && (
            <p className="font-badge text-xs text-[#52525B] mt-4" data-testid="search-no-results">
              {t("noResults")}
            </p>
          )}
          {results && results.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="search-results-grid">
              {results.slice(0, 8).map((p) => (
                <Link
                  key={p.id}
                  to={`/item/${p.id}`}
                  onClick={onClose}
                  data-testid={`search-result-${p.id}`}
                  className="border border-[#09090B] bg-white group"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#E4E4E7] border-b border-[#09090B]">
                    <img
                      src={p.images?.[0]}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-semibold uppercase tracking-tight leading-snug">{p.name}</p>
                    <p className="font-['Space_Grotesk'] text-base font-extrabold mt-1">{formatPrice(p.price, "eur")}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
